import React, { useCallback, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Business, ExpandMore, Home } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type {
  Dashboard2DayBookingSummary,
  DayWorkStatus,
  DayWorkStatusKind
} from "../types/dashboard2-work-status.types";
import {
  dashboard2TodayWorkStatusCollapseSx,
  dashboard2TodayWorkStatusPlanTriggerSx,
  dashboard2TodayWorkStatusTriggerLabelSx,
  dashboard2TodayWorkStatusTriggerSx
} from "../dashboard2-status.styles";
import { todayWorkStatusTriggerLabel } from "../utils/dashboard2-status-display.utils";
import { Dashboard2StatusSheet } from "./Dashboard2StatusSheet";

type Dashboard2TodayWorkStatusCollapseProps = {
  status: DayWorkStatus;
  onSelectStatus?: (kind: DayWorkStatusKind) => void;
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
  primarySchedule?: BookingScheduleInterface;
  onPrimaryScheduleClick?: (schedule: BookingScheduleInterface) => void;
  mobileWorkingEnabled?: boolean;
  disabled?: boolean;
};

const StatusActionIcon: React.FC<{ kind: DayWorkStatusKind }> = ({ kind }) => {
  if (kind === "unplanned") {
    return null;
  }

  if (kind === "mobile_working") {
    return <Home fontSize="small" />;
  }

  if (kind === "office") {
    return <Business fontSize="small" />;
  }

  return <Business fontSize="small" />;
};

export const Dashboard2TodayWorkStatusCollapse: React.FC<
  Dashboard2TodayWorkStatusCollapseProps
> = ({
  status,
  onSelectStatus,
  onBookingClick,
  primarySchedule,
  onPrimaryScheduleClick,
  mobileWorkingEnabled = true,
  disabled = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [sheetOpen, setSheetOpen] = useState(false);
  const triggerLabel = todayWorkStatusTriggerLabel(status, t);
  const isPlanTodayTrigger = status.kind === "unplanned";

  const handleOpenSheet = useCallback(() => {
    setSheetOpen(true);
  }, []);

  const handleCloseSheet = useCallback(() => {
    setSheetOpen(false);
  }, []);

  const handleSelectStatus = useCallback(
    (kind: DayWorkStatusKind) => {
      if (onSelectStatus) {
        onSelectStatus(kind);
        setSheetOpen(false);
      }
    },
    [onSelectStatus]
  );

  return (
    <>
      <Box
        sx={dashboard2TodayWorkStatusCollapseSx}
        data-testid="dashboard2-today-work-status-collapse"
      >
        <Box
          component="button"
          type="button"
          sx={
            isPlanTodayTrigger
              ? dashboard2TodayWorkStatusPlanTriggerSx(theme)
              : dashboard2TodayWorkStatusTriggerSx(theme)
          }
          onClick={handleOpenSheet}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          aria-label={t("_dashboard2_todayWorkStatusTriggerAria", { status: triggerLabel })}
          data-testid="dashboard2-today-work-status-trigger"
        >
          <Box sx={dashboard2TodayWorkStatusTriggerLabelSx}>
            <StatusActionIcon kind={status.kind} />
            <Typography variant="body2" fontWeight={600} noWrap>
              {triggerLabel}
            </Typography>
          </Box>
          <ExpandMore fontSize="small" aria-hidden />
        </Box>
      </Box>

      <Dashboard2StatusSheet
        open={sheetOpen}
        dayLabel={t("_dashboard2_todayFieldLabel")}
        currentStatus={status}
        onClose={handleCloseSheet}
        onSelectStatus={handleSelectStatus}
        onBookingClick={onBookingClick}
        primarySchedule={primarySchedule}
        onPrimaryScheduleClick={onPrimaryScheduleClick}
        mobileWorkingEnabled={mobileWorkingEnabled}
      />
    </>
  );
};
